import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import './ConnectMcp.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

export default function ConnectMcp({ isProAccount, isFounder }) {
    const { username } = useRevit();
    const { getToken } = useAuth();
    const [params] = useSearchParams();
    const requestId = params.get('request');
    const [clientName, setClientName] = useState('');
    const [status, setStatus] = useState(requestId ? 'loading' : 'missing');
    const [error, setError] = useState('');
    const canConnect = isProAccount || isFounder;

    useEffect(() => {
        if (!requestId) return undefined;
        let cancelled = false;

        (async () => {
            try {
                const res = await fetch(`https://${SERVER_URL}/api/oauth/request/${encodeURIComponent(requestId)}`);
                const body = await res.json().catch(() => ({}));
                if (cancelled) return;
                if (!res.ok) {
                    setError(body.error || 'This connection request has expired.');
                    setStatus('error');
                    return;
                }
                setClientName(body.clientName || 'An AI app');
                setStatus('ready');
            } catch {
                if (!cancelled) {
                    setError('Could not reach RevTrack.');
                    setStatus('error');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [requestId]);

    const approve = async () => {
        setStatus('saving');
        try {
            const token = await getToken();
            const res = await fetch(`https://${SERVER_URL}/api/oauth/consent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ requestId, username }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(body.error || 'Could not approve the connection.');
                setStatus('error');
                return;
            }
            window.location.assign(body.redirectUrl);
        } catch {
            setError('Could not reach RevTrack.');
            setStatus('error');
        }
    };

    return (
        <div className="connect-mcp">
            <div className="connect-mcp__card">
                <h1 className="heading-h4">Connect RevTrack</h1>
                {status === 'missing' && (
                    <p>Open Claude or ChatGPT and add the connector again. This page opens from that sign-in step.</p>
                )}
                {status === 'loading' && <p>Loading the connection request…</p>}
                {status === 'error' && <p className="connect-mcp__error">{error}</p>}
                {(status === 'ready' || status === 'saving') && (
                    <>
                        <p className="body-medium-regular">
                            <strong>{clientName}</strong> is asking to read your RevTrack offers, clicks, and conversions.
                        </p>
                        {canConnect ? (
                            <button className="connect-mcp__allow" type="button" onClick={approve} disabled={status === 'saving'}>
                                {status === 'saving' ? 'Connecting…' : 'Allow access'}
                            </button>
                        ) : (
                            <p className="connect-mcp__error">A Pro plan is required to connect an AI app.</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
